import { AutoMode, Close } from '@mui/icons-material';
import {
  Button, Dialog, IconButton, useMediaQuery, useTheme,
} from '@mui/material';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import * as React from 'react';
import { useState } from 'react';
import { VARIANTS } from 'shared/constants';
import { getOrderedVlanBuildingRows, updateComplexVlans } from 'shared/api/complexes';
import { useNotification, useRequest } from 'shared/hooks';
import VlanEditDataGrid from 'features/networking/complex/VlanDataGrid';

/**
 * For VLAN redistribution.
 * @param {number} complexId - ID of the complex
 * @param {number} vlanMargin - VLAN margin for redistribution
 * @param {number} minVlan - Minimum VLAN value
 * @param {number} maxVlan - Maximum VLAN value
 * @param {string} vlanDistributionType - Type of VLAN distribution
 * @param {boolean} hasBuildings - Whether the complex has buildings
 */
export default function VlanEditDialog({
  complexId, vlanMargin, minVlan, maxVlan, vlanDistributionType, hasBuildings,
}) {
  const { request } = useRequest();
  const [isOpen, setIsOpen] = useState(false);
  const theme = useTheme();
  const { pushSuccess } = useNotification();
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'));
  const [orderedBuildings, setOrderedBuildings] = useState();

  const handleClick = async () => {
    const result = await request(getOrderedVlanBuildingRows(complexId, {
      vlan_margin: vlanMargin,
      min_vlan: minVlan,
      max_vlan: maxVlan,
    }));
    if (result) {
      const indexedBuildings = result.map((building, index) => ({
        ...building,
        index,
      }));
      setOrderedBuildings(indexedBuildings);
    }
    setIsOpen(true);
  };

  const handleSave = async (rows) => {
    const buildingVlanChangeRequests = rows.map((building) => ({
      building_id: building.id,
      vlan_start: parseInt(building.vlan_range_start, 10),
      vlan_end: parseInt(building.vlan_range_end, 10),
      vlan_distribution_type: vlanDistributionType,
    }));
    const results = await request(updateComplexVlans(complexId, {
      building_changes: buildingVlanChangeRequests,
    }));
    if (results) {
      pushSuccess('Building Vlans updated');
      setOrderedBuildings(rows);
    }
    setIsOpen(false);
  };

  return (
    <>
      <Button
        variant={VARIANTS.contained}
        startIcon={<AutoMode />}
        onClick={() => handleClick()}
        disabled={!hasBuildings}
      >
        Redistribute
      </Button>
      {orderedBuildings && (
        <Dialog
          open={isOpen}
          keepMounted
          fullWidth
          maxWidth="lg"
          fullScreen={fullScreen}
        >
          <DialogTitle>
            Planned Vlan Redistribution
            <IconButton
              onClick={() => {
                setIsOpen(false);
                setOrderedBuildings(null);
              }}
              sx={{ position: 'absolute', right: 8, top: 8 }}
            >
              <Close />
            </IconButton>
          </DialogTitle>
          <DialogContent>
            <VlanEditDataGrid
              orderedBuildings={orderedBuildings}
              complexId={complexId}
              minVlan={minVlan}
              maxVlan={maxVlan}
              vlanMargin={vlanMargin}
              onSave={handleSave}
            />
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}
